import React from 'react'
import type { WatchlistList } from '../../services/watchlist'
import WatchlistCreateListInline from './WatchlistCreateListInline'

interface WatchlistListPickerProps {
  lists: WatchlistList[]
  selectedListIds: number[]
  onChange: (listIds: number[]) => void
  idPrefix?: string
  onCreateList?: (name: string) => Promise<WatchlistList>
}

/**
 * Checkbox list: "Ingen kategorisering" (empty listIds) or one/more project lists.
 */
const WatchlistListPicker: React.FC<WatchlistListPickerProps> = ({
  lists,
  selectedListIds,
  onChange,
  idPrefix = 'wl',
  onCreateList,
}) => {
  const uncategorized = selectedListIds.length === 0

  function toggleList(listId: number, checked: boolean) {
    if (checked) {
      onChange([...selectedListIds, listId])
      return
    }
    onChange(selectedListIds.filter((id) => id !== listId))
  }

  return (
    <div
      role="group"
      aria-label="Vælg lister"
      className="space-y-2 min-w-0 w-full overflow-hidden"
    >
      <label className="flex items-center gap-2 min-w-0 text-sm text-ink cursor-pointer">
        <input
          type="checkbox"
          id={`${idPrefix}-none`}
          checked={uncategorized}
          onChange={() => onChange([])}
          className="shrink-0 rounded-sm border-rule text-signal focus:ring-signal"
        />
        <span className="min-w-0 truncate">Ingen kategorisering</span>
      </label>
      {lists.length > 0 && (
        <ul className="space-y-1.5 pt-1 border-t border-rule min-w-0">
          {lists.map((list) => {
            const checked = selectedListIds.includes(list.id)
            return (
              <li key={list.id} className="min-w-0">
                <label className="flex items-center gap-2 min-w-0 text-sm text-ink cursor-pointer">
                  <input
                    type="checkbox"
                    id={`${idPrefix}-list-${list.id}`}
                    checked={checked}
                    onChange={(e) => toggleList(list.id, e.target.checked)}
                    className="shrink-0 rounded-sm border-rule text-signal focus:ring-signal"
                  />
                  <span className="min-w-0 flex-1 truncate" title={list.name}>
                    {list.name}
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
      )}
      {onCreateList && (
        <div className={`${lists.length > 0 ? 'pt-1' : 'pt-1 border-t border-rule'} min-w-0`}>
          <WatchlistCreateListInline
            compact
            inputId={`${idPrefix}-new-list`}
            onCreate={onCreateList}
            onCreated={(list) => onChange([...selectedListIds, list.id])}
          />
        </div>
      )}
    </div>
  )
}

export default WatchlistListPicker
